import React, { useEffect, useMemo, useState } from 'react';
import SmallButton from '../../../components/SmallButton';
import { axiosKK } from '../../../services/networkRequest';
import { downloadCsv } from '../../../services/downloadBlob';
import errorMessage from '../../../services/errorMessage';
import { OrderIssueType, StageProgress } from '../../../models/APIModels';

type SubTab = 'inbound' | 'outbound' | 'storage' | 'box-history';

interface InboundOrderRow {
  orderId?: number;
  customer?: string;
  courier?: string;
  date?: string;
  boxes?: number;
  status?: string;
  unloadProgress?: StageProgress;
  issues?: { type: OrderIssueType }[];
  [k: string]: unknown;
}
interface OutboundOrderRow {
  orderId?: number;
  customer?: string;
  courier?: string;
  date?: string;
  boxes?: number;
  status?: string;
  pickProgress?: StageProgress;
  holdoutProgress?: StageProgress;
  onsProgress?: StageProgress;
  issues?: { type: OrderIssueType }[];
  [k: string]: unknown;
}
interface BoxHistoryRow {
  bid?: string;
  orderId?: number;
  customer?: string;
  location?: string;
  lastScan?: string;
  [k: string]: unknown;
}
interface StorageRow {
  orderId?: number;
  customer?: string;
  boxes?: number;
  months?: number;
  amount?: number;
  [k: string]: unknown;
}

function monthISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function todayISO(): string {
  return new Date().toISOString().split('T')[0];
}

interface DynamicTableProps<T extends Record<string, unknown>> {
  rows: T[];
  hiddenKeys?: string[];
  emptyMessage?: string;
}

function DynamicTable<T extends Record<string, unknown>>({
  rows,
  hiddenKeys = [],
  emptyMessage = 'No rows.',
}: DynamicTableProps<T>) {
  const keys = useMemo(() => {
    const set = new Set<string>();
    rows.slice(0, 5).forEach((r) =>
      Object.keys(r).forEach((k) => !hiddenKeys.includes(k) && set.add(k))
    );
    return Array.from(set);
  }, [rows, hiddenKeys]);

  if (rows.length === 0) return <p>{emptyMessage}</p>;

  const renderCell = (value: unknown): React.ReactNode => {
    if (value == null) return '—';
    if (typeof value === 'object') {
      const progress = value as { scanned?: number; total?: number };
      if ('scanned' in progress && 'total' in progress) {
        return `${progress.scanned ?? 0}/${progress.total ?? 0}`;
      }
      if (Array.isArray(value)) {
        return value.length === 0 ? '—' : `${value.length} item(s)`;
      }
      return JSON.stringify(value);
    }
    return String(value);
  };

  return (
    <table className="pricing-table">
      <thead>
        <tr>
          {keys.map((k) => (
            <th key={k}>{k}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i}>
            {keys.map((k) => (
              <td key={k}>{renderCell(row[k])}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function WarehouseTab() {
  const [tab, setTab] = useState<SubTab>('inbound');

  const [date, setDate] = useState<string>(todayISO);
  const [month, setMonth] = useState<string>(monthISO);
  const [search, setSearch] = useState('');
  const [issueFilter, setIssueFilter] = useState<OrderIssueType | ''>('');

  const [inbound, setInbound] = useState<InboundOrderRow[]>([]);
  const [outbound, setOutbound] = useState<OutboundOrderRow[]>([]);
  const [storage, setStorage] = useState<StorageRow[]>([]);
  const [history_, setHistory] = useState<BoxHistoryRow[]>([]);

  const [loading, setLoading] = useState(false);
  const [netError, setNetError] = useState<string>();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNetError(undefined);

    const endpointByTab: Record<SubTab, { url: string; params: Record<string, unknown> }> = {
      inbound: {
        url: '/logistics/dashboard/warehouse/inbound',
        params: { date, ...(issueFilter ? { issue: issueFilter } : {}) },
      },
      outbound: {
        url: '/logistics/dashboard/warehouse/outbound',
        params: { date, ...(issueFilter ? { issue: issueFilter } : {}) },
      },
      storage: {
        url: '/logistics/dashboard/warehouse/storage',
        params: { month },
      },
      'box-history': {
        url: '/logistics/dashboard/warehouse/box-history',
        params: { month, ...(search ? { search } : {}) },
      },
    };

    const { url, params } = endpointByTab[tab];
    axiosKK
      .get<any>(url, { params })
      .then(({ data }) => {
        if (cancelled) return;
        if (tab === 'inbound') setInbound(data.orders || data.rows || []);
        else if (tab === 'outbound') setOutbound(data.orders || data.rows || []);
        else if (tab === 'storage') setStorage(data.rows || data.orders || []);
        else setHistory(data.rows || data.boxes || data.items || []);
      })
      .catch((err) => {
        if (!cancelled) errorMessage(setNetError)(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [tab, date, month, search, issueFilter]);

  const exportCurrent = () => {
    const csvByTab: Record<SubTab, { url: string; params: Record<string, unknown>; filename: string }> = {
      inbound: {
        url: '/logistics/dashboard/warehouse/inbound',
        params: { date, ...(issueFilter ? { issue: issueFilter } : {}) },
        filename: `warehouse-inbound-${date}.csv`,
      },
      outbound: {
        url: '/logistics/dashboard/warehouse/outbound',
        params: { date, ...(issueFilter ? { issue: issueFilter } : {}) },
        filename: `warehouse-outbound-${date}.csv`,
      },
      storage: {
        url: '/logistics/dashboard/warehouse/storage',
        params: { month },
        filename: `warehouse-storage-${month}.csv`,
      },
      'box-history': {
        url: '/logistics/dashboard/warehouse/box-history',
        params: { month, ...(search ? { search } : {}) },
        filename: `warehouse-box-history-${month}.csv`,
      },
    };
    const { url, params, filename } = csvByTab[tab];
    downloadCsv(url, filename, params).catch((err) => errorMessage(setNetError)(err));
  };

  return (
    <>
      <div className="admin-tab-bar admin-tab-bar--sub">
        <button
          type="button"
          className={tab === 'inbound' ? 'admin-tab-bar__tab admin-tab-bar__tab--active' : 'admin-tab-bar__tab'}
          onClick={() => setTab('inbound')}
        >
          Inbound
        </button>
        <button
          type="button"
          className={tab === 'outbound' ? 'admin-tab-bar__tab admin-tab-bar__tab--active' : 'admin-tab-bar__tab'}
          onClick={() => setTab('outbound')}
        >
          Outbound
        </button>
        <button
          type="button"
          className={tab === 'storage' ? 'admin-tab-bar__tab admin-tab-bar__tab--active' : 'admin-tab-bar__tab'}
          onClick={() => setTab('storage')}
        >
          Storage
        </button>
        <button
          type="button"
          className={tab === 'box-history' ? 'admin-tab-bar__tab admin-tab-bar__tab--active' : 'admin-tab-bar__tab'}
          onClick={() => setTab('box-history')}
        >
          Box History
        </button>
      </div>

      {netError && <p className="error">{netError}</p>}

      <section className="admin-warehouse-forecast">
        <div className="logistics-controls-container">
          <div className="filters-group">
            {(tab === 'inbound' || tab === 'outbound') && (
              <>
                <div className="filter-item">
                  <label>Date</label>
                  <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                </div>
                <div className="filter-item">
                  <label>Issue</label>
                  <select
                    value={issueFilter}
                    onChange={(e) => setIssueFilter(e.target.value as OrderIssueType | '')}
                  >
                    <option value="">All</option>
                    <option value="damaged">Damaged</option>
                    <option value="overweight">Overweight</option>
                    <option value="repack">Repack</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </>
            )}
            {(tab === 'storage' || tab === 'box-history') && (
              <div className="filter-item">
                <label>Month</label>
                <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
              </div>
            )}
            {tab === 'box-history' && (
              <div className="filter-item">
                <label>Search</label>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="BID or order id"
                />
              </div>
            )}
            <div className="filter-item filter-item--action">
              <SmallButton onClick={exportCurrent}>
                <i className="fas fa-file-csv" /> Export CSV
              </SmallButton>
            </div>
          </div>
        </div>

        {loading ? (
          <p>Loading…</p>
        ) : tab === 'inbound' ? (
          <DynamicTable rows={inbound} />
        ) : tab === 'outbound' ? (
          <DynamicTable rows={outbound} />
        ) : tab === 'storage' ? (
          <DynamicTable rows={storage} />
        ) : (
          <DynamicTable rows={history_} />
        )}
      </section>
    </>
  );
}
